import { addDays, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";
import { config } from "@/config";
import { perkpointApi as perkPointClient } from "./perkpoint/client";

export type AuditEvent =
  | "created"
  | "updated"
  | "deleted"
  | "submitted"
  | "approved"
  | "rejected"
  | "status_changed"
  | "reversed"
  | "exported"
  | "points"
  | "other";

export type AuditActor = {
  type: "admin" | "system" | "api";
  /** The value the `user_id` filter takes: an email, `system`, or `api`. */
  id: string | null;
  name: string;
  email: string | null;
};

export type AuditChange = {
  field: string;
  label: string;
  from: unknown;
  to: unknown;
};

export type AuditEntry = {
  id: string;
  seq: number;
  created_at: string;
  program: { id: string; name: string };
  action: string;
  event: AuditEvent;
  event_label: string;
  object_type: string;
  object_label: string;
  object_id: string;
  object_name: string | null;
  entity: { type: string; id: string; label: string | null; code: string | null };
  user: AuditActor;
  details: string;
  changes: AuditChange[];
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  ip_address: string | null;
  user_agent: string | null;
  hash: string;
  prev_hash: string;
  /** Rewardchain transaction and block, once the entry is written on-chain. */
  tx_hash: string | null;
  block_number: number | null;
};

export type AuditOption = { value: string; label: string; count: number };

export type AuditFacets = {
  events: AuditOption[];
  object_types: AuditOption[];
  users: (AuditOption & { id: string; name: string; email: string | null })[];
};

export type AuditListResponse = {
  items: AuditEntry[];
  total: number;
  page: number;
  page_size: number;
  facets?: AuditFacets;
};

export type AuditQuery = {
  /** Omit for every program. */
  programId?: string | null;
  search?: string;
  event?: string;
  object_type?: string;
  user_id?: string;
  /** ISO date-times; `to` is exclusive. */
  from?: string;
  to?: string;
  sort?: "asc" | "desc";
  page?: number;
  page_size?: number;
  facets?: boolean;
};

export type AuditVerification = {
  program_id: string;
  verified: boolean;
  checked: number;
  last_seq?: number;
  last_hash?: string | null;
  broken_at?: { id: string; seq: number; reason: string };
};

export type PerkPointProgramSummary = { id: string; name: string; status: string };

function clean(params: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
}

export class PerkPointAuditApi {
  static async list({ programId, facets, ...query }: AuditQuery) {
    const path = programId
      ? `/admin/programs/${programId}/audit`
      : "/admin/audit";
    const response = await perkPointClient.get<AuditListResponse>(path, {
      params: clean({ ...query, facets: facets === false ? "false" : undefined }),
    });
    return response.data;
  }

  static async get(entryId: string) {
    const response = await perkPointClient.get<AuditEntry>(`/admin/audit/${entryId}`);
    return response.data;
  }

  static async verify(programId: string) {
    const response = await perkPointClient.get<AuditVerification>(
      `/admin/programs/${programId}/audit/verify`,
    );
    return response.data;
  }

  /** Programs on the Perk Point backend, for the program filter. */
  static async programs() {
    const response = await perkPointClient.get<{ items: PerkPointProgramSummary[] }>(
      "/admin/programs",
      { params: { page_size: 100 } },
    );
    return response.data.items ?? [];
  }
}

/** The Rewardchain explorer, from VITE_BLOCKCHAIN_EXPLORER_URL. Empty when not set. */
export const blockchainExplorerUrl = config.BLOCKCHAIN_EXPLORER_URL.replace(/\/$/, "");

/**
 * Explorer page for the transaction that recorded this entry on Rewardchain.
 * Null until the entry has been written on-chain.
 */
export function auditExplorerUrl(entry: Pick<AuditEntry, "tx_hash">): string | null {
  if (!blockchainExplorerUrl || !entry.tx_hash) return null;
  return `${blockchainExplorerUrl}/tx/${entry.tx_hash}`;
}

/** A picked date range as the API expects it: local whole days, `to` exclusive. */
export function auditRangeParams(range: DateRange | undefined): { from?: string; to?: string } {
  if (!range?.from) return {};
  return {
    from: startOfDay(range.from).toISOString(),
    to: startOfDay(addDays(range.to ?? range.from, 1)).toISOString(),
  };
}
